import { useEffect, useState } from 'react';
import { useListContext } from '../ListContext';
import SearchInputLayout, { DEFAULT_SEARCH_INPUT_ID } from './SearchInputLayout';
import { useLocation } from 'react-router-dom';
import { SearchInputProps } from './SearchInput.types';

const TYPING_TIMEOUT = 600;
const MAX_SUGGESTED_QUERIES = 5;

/**
 * Search Input
 * @param label
 * @param placeholder
 * @param typingTimeout interval for user typing (within this time, search won't be applied).
 * @param maxSuggestedQueries - max count of saved suggestions.
 * @param valueDecorator - decorator for search value
 * @param tooltipMessage - tooltip message for search input
 * @param inputRef
 * @param isAutoCompleteEnabled
 * @param onKeyUpCallback
 * @param onFocusCallback
 * @param id - input id
 * @param className
 * @param width
 * @param cleanUpValueWhenChangedFilters
 */

const SearchInput = ({
  label,
  placeholder = '',
  typingTimeout = TYPING_TIMEOUT,
  maxSuggestedQueries = MAX_SUGGESTED_QUERIES,
  valueDecorator = undefined,
  tooltipMessage = undefined,
  inputRef = {
    current: null,
  },
  isAutoCompleteEnabled = true,
  onKeyUpCallback = () => {},
  onFocusCallback = () => {},
  id = DEFAULT_SEARCH_INPUT_ID,
  className,
  width,
  cleanUpValueWhenChangedFilters = false,
}: SearchInputProps) => {
  const { searchBy: applySearch, search = '' } = useListContext();
  const [searchText, setSearchText] = useState(search);
  const [applyingTimeout, setApplyingTimeout] = useState<number>();
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const { pathname } = useLocation();

  useEffect(() => {
    if (cleanUpValueWhenChangedFilters && !search && searchText) {
      setSearchText('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanUpValueWhenChangedFilters, search]);

  const localStorageKey = `${pathname}_suggestions`;

  const addSuggestion = (suggestion: string) => {
    if (!suggestions.includes(suggestion) && suggestion !== '') {
      setSuggestions(prev => [suggestion, ...prev].slice(0, maxSuggestedQueries));
    }
  };

  /**
   * Load suggestions from local storage
   */
  useEffect(() => {
    setSuggestions(JSON.parse(localStorage.getItem(localStorageKey) as string) || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Update suggestions in local storage
   */
  useEffect(() => {
    localStorage.setItem(localStorageKey, JSON.stringify(suggestions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions]);

  const onChangeCallback = (event: CustomFormEvent<string>) => {
    const inputValue = event.target.value || '';
    const value = valueDecorator ? valueDecorator(inputValue) : inputValue;
    setSearchText(value);

    if (applyingTimeout) clearTimeout(applyingTimeout);
    const timeout = setTimeout(() => {
      if (applySearch) {
        applySearch(value.trim());
      }
      addSuggestion(value.trim());
    }, typingTimeout);

    setApplyingTimeout(timeout as any);
  };

  return (
    <SearchInputLayout
      onChangeCallback={onChangeCallback}
      value={searchText}
      label={label}
      placeholder={placeholder}
      suggestions={suggestions}
      tooltipMessage={tooltipMessage}
      onKeyUpCallback={onKeyUpCallback}
      onFocusCallback={onFocusCallback}
      inputRef={inputRef}
      isAutoCompleteEnabled={isAutoCompleteEnabled}
      id={id}
      className={className}
      width={width}
    />
  );
};

export default SearchInput;
